import {
    Box,
    Divider,
    IconButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Menu,
    MenuItem,
    Tooltip,
} from '@mui/material';
import React, { useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import ChatTitle from '../ChatTitle';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import Locale from '/b2b/common/components/Locale';
import MenuIcon from '@mui/icons-material/Menu';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { PANEL_MODES } from '../../state/reducer';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PictureInPictureAltIcon from '@mui/icons-material/PictureInPictureAlt';
import PropTypes from 'prop-types';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import makeStyles from '@mui/styles/makeStyles';
import styles from './PanelHeader.styles';
import { translate } from '/b2b/common/helpers/i18n';

const useStyles = makeStyles(styles);

/*
 * Header for the panel modes (sidebar/pop-up). Shows a chats menu, a new-chat
 * button (while in an active chat), the active conversation title (or "New
 * chat"), an ellipsis actions menu (consult privacy team / delete, active chats
 * only), a view switcher (side panel / pop-up / full screen), and a close control
 * that dismisses the overlay. The switcher's side-panel and pop-up actions swap
 * the panel mode in place; full screen and the actions navigate/act via callbacks
 * the host supplies, so this stays presentational.
 */
const PanelHeader = props => {
    const {
        title,
        activeConversationId,
        panelMode,
        onOpenChats,
        onNewChat,
        onSetSidePanel,
        onSetPopup,
        onOpenFullScreen,
        onClose,
        canEscalate,
        onEscalate,
        onDelete,
    } = props;
    const classes = useStyles(props);

    const [viewAnchorEl, setViewAnchorEl] = useState(null);
    const viewMenuOpen = Boolean(viewAnchorEl);
    const openViewMenu = event => setViewAnchorEl(event.currentTarget);
    const closeViewMenu = () => setViewAnchorEl(null);
    const selectView = action => {
        closeViewMenu();
        if (action) action();
    };

    // Ellipsis actions menu (rename/consult/delete) for the active chat. Rename is
    // deferred (no backend support yet), so only consult + delete are wired.
    const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
    const actionsMenuOpen = Boolean(actionsAnchorEl);
    const openActionsMenu = event => setActionsAnchorEl(event.currentTarget);
    const closeActionsMenu = () => setActionsAnchorEl(null);
    const selectAction = action => {
        closeActionsMenu();
        if (action) action();
    };

    return (
        <Box className={classes.header}>
            <Box className={classes.leftZone}>
                <Tooltip title={translate('aiAssistant.buttons.chats.label')}>
                    <IconButton
                        className={classes.iconButton}
                        size="small"
                        onClick={onOpenChats}
                        aria-label={translate('aiAssistant.buttons.chats.label')}
                    >
                        <MenuIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                {activeConversationId && (
                    <Tooltip title={translate('aiAssistant.buttons.newChat.ariaLabel')}>
                        <IconButton
                            className={classes.iconButton}
                            size="small"
                            onClick={onNewChat}
                            aria-label={translate('aiAssistant.buttons.newChat.ariaLabel')}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            <Box className={classes.titleGroup}>
                <ChatTitle
                    title={title}
                    placeholder={<Locale path="aiAssistant.header.newChat" />}
                    className={classes.title}
                />
                {activeConversationId && (
                    <>
                        <Tooltip title={translate('aiAssistant.buttons.chatActions.ariaLabel')}>
                            <IconButton
                                className={classes.chatActions}
                                onClick={openActionsMenu}
                                size="small"
                                aria-label={translate('aiAssistant.buttons.chatActions.ariaLabel')}
                                aria-haspopup="true"
                                aria-expanded={actionsMenuOpen}
                            >
                                <MoreHorizIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Menu
                            anchorEl={actionsAnchorEl}
                            open={actionsMenuOpen}
                            onClose={closeActionsMenu}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        >
                            {canEscalate && (
                                <MenuItem onClick={() => selectAction(onEscalate)}>
                                    <ListItemIcon>
                                        <PersonOutlineIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>
                                        {translate('aiAssistant.buttons.escalate.label')}
                                    </ListItemText>
                                </MenuItem>
                            )}
                            {canEscalate && <Divider />}
                            <MenuItem
                                onClick={() => selectAction(onDelete)}
                                sx={{ color: 'error.main' }}
                            >
                                <ListItemIcon>
                                    <DeleteOutlineIcon fontSize="small" color="error" />
                                </ListItemIcon>
                                <ListItemText primaryTypographyProps={{ color: 'error.main' }}>
                                    {translate('aiAssistant.buttons.deleteChat.label')}
                                </ListItemText>
                            </MenuItem>
                        </Menu>
                    </>
                )}
            </Box>
            <Box className={classes.rightZone}>
                <Tooltip title={translate('aiAssistant.buttons.viewSwitcher.ariaLabel')}>
                    <IconButton
                        className={classes.viewSwitcher}
                        onClick={openViewMenu}
                        size="small"
                        aria-label={translate('aiAssistant.buttons.viewSwitcher.ariaLabel')}
                        aria-haspopup="true"
                        aria-expanded={viewMenuOpen}
                    >
                        <FullscreenIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Menu
                    anchorEl={viewAnchorEl}
                    open={viewMenuOpen}
                    onClose={closeViewMenu}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <ListSubheader disableSticky className={classes.viewMenuHeading}>
                        {translate('aiAssistant.viewSwitcher.heading')}
                    </ListSubheader>
                    <MenuItem onClick={() => selectView(onSetSidePanel)}>
                        <ListItemIcon>
                            <ViewSidebarIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            {translate('aiAssistant.viewSwitcher.sidePanel')}
                        </ListItemText>
                        {panelMode === PANEL_MODES.SIDEBAR && (
                            <CheckIcon fontSize="small" className={classes.viewCheck} />
                        )}
                    </MenuItem>
                    <MenuItem onClick={() => selectView(onSetPopup)}>
                        <ListItemIcon>
                            <PictureInPictureAltIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>{translate('aiAssistant.viewSwitcher.popup')}</ListItemText>
                        {panelMode === PANEL_MODES.WIDGET && (
                            <CheckIcon fontSize="small" className={classes.viewCheck} />
                        )}
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => selectView(onOpenFullScreen)}>
                        <ListItemIcon>
                            <FullscreenIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>
                            {translate('aiAssistant.viewSwitcher.fullScreen')}
                        </ListItemText>
                    </MenuItem>
                </Menu>
                <Tooltip title={translate('common.close')}>
                    <IconButton
                        className={classes.close}
                        onClick={onClose}
                        size="small"
                        aria-label={translate('common.close')}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
};

PanelHeader.propTypes = {
    title: PropTypes.string,
    activeConversationId: PropTypes.string,
    panelMode: PropTypes.oneOf(Object.values(PANEL_MODES)),
    onOpenChats: PropTypes.func.isRequired,
    onNewChat: PropTypes.func.isRequired,
    onSetSidePanel: PropTypes.func,
    onSetPopup: PropTypes.func,
    onOpenFullScreen: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    canEscalate: PropTypes.bool,
    onEscalate: PropTypes.func,
    onDelete: PropTypes.func,
};

export default PanelHeader;
